import React, { useState, useEffect } from 'react';
import { getSupabaseClient } from '@/src/lib/supabase';
import { ShoppingBag, Clock, Bed, CreditCard, Loader2, CheckCircle2, AlertCircle, UtensilsCrossed, MapPin, Wrench, Calendar, IndianRupee, Key, X } from 'lucide-react';
import { Card, CardContent } from '@/src/components/ui/card';
import { Button } from '@/src/components/ui/button';
import { LiveTrackingMap } from './LiveTrackingMap';
import { payWithRazorpay, pollPaymentStatus } from '@/src/lib/razorpay';
import { toast } from 'sonner';

export function OrderHistoryView({
  userName,
  tenant,
  initialTab = 'orders'
}: {
  userName?: string;
  tenant?: any;
  initialTab?: 'orders' | 'bookings' | 'table_bookings' | 'services';
} = {}) {
  const [activeTab, setActiveTab] = useState<'orders' | 'bookings' | 'table_bookings' | 'services'>(initialTab);
  const [orders, setOrders] = useState<any[]>([]);
  const [bookings, setBookings] = useState<any[]>([]);
  const [tableBookings, setTableBookings] = useState<any[]>([]);
  const [serviceBookings, setServiceBookings] = useState<any[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [loadingTableBookings, setLoadingTableBookings] = useState(false);
  const [loadingServiceBookings, setLoadingServiceBookings] = useState(false);
  const [selectedServiceDetail, setSelectedServiceDetail] = useState<any | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [payingBookingId, setPayingBookingId] = useState<string | null>(null);
  const [payingOrderId, setPayingOrderId] = useState<string | null>(null);

  const fetchOrders = async () => {
    try {
      const supabase = getSupabaseClient();
      if (!supabase) return;

      const { data: session } = await supabase.auth.getSession();
      const userId = session?.session?.user?.id;
      if (!userId) {
        setOrders([]);
        return;
      }
      setCurrentUserId(userId);

      const { data: ordersData, error } = await supabase
        .from('orders')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error || !ordersData || ordersData.length === 0) {
        setOrders([]);
        return;
      }

      const orderIds = ordersData.map((o: any) => o.id);
      const { data: itemsData } = await supabase.from('order_items').select('*').in('order_id', orderIds);

      let productsData: any[] = [];
      if (itemsData && itemsData.length > 0) {
        const productIds = [...new Set(itemsData.map((i: any) => i.product_id))];
        const { data: pData } = await supabase.from('products').select('id, product_name').in('id', productIds);
        productsData = pData || [];
      }

      const pMap = new globalThis.Map(productsData.map((p) => [p.id, p.product_name]));

      const { data: daData } = await supabase
        .from('delivery_assignments')
        .select('*, service_providers(id, full_name, current_lat, current_lng)')
        .in('order_id', orderIds);

      const mergedOrders = ordersData.map((o: any) => {
        const oItems = itemsData ? itemsData.filter((i: any) => i.order_id === o.id) : [];
        return {
          ...o,
          total_amount: o.total,
          delivery_assignment: daData ? daData.find((d: any) => d.order_id === o.id) : null,
          order_items: oItems.map((i: any) => ({
            ...i,
            products: { name: pMap.get(i.product_id) || 'Unknown Product' }
          }))
        };
      });

      setOrders(mergedOrders);
    } catch (err) {
      console.warn('Failed to fetch order history:', err);
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchBookings = async () => {
    setLoadingBookings(true);
    try {
      const supabase = getSupabaseClient();
      if (!supabase) return;

      const { data: session } = await supabase.auth.getSession();
      const userId = session?.session?.user?.id;
      if (!userId) {
        setBookings([]);
        return;
      }
      setCurrentUserId(userId);

      // Attempt rich join
      let bookingsData: any[] = [];
      const { data, error } = await supabase
        .from('room_bookings')
        .select('*, room_types(name), branches(branch_name, address)')
        .eq('customer_user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        // Fallback without relations if FK differs
        const { data: fallbackData } = await supabase
          .from('room_bookings')
          .select('*')
          .eq('customer_user_id', userId)
          .order('created_at', { ascending: false });
        bookingsData = fallbackData || [];
      } else {
        bookingsData = data || [];
      }

      setBookings(bookingsData);
    } catch (err) {
      console.warn('Failed to fetch room bookings:', err);
    } finally {
      setLoadingBookings(false);
    }
  };

  const fetchTableBookings = async () => {
    setLoadingTableBookings(true);
    try {
      const supabase = getSupabaseClient();
      if (!supabase) return;

      const { data: session } = await supabase.auth.getSession();
      const userId = session?.session?.user?.id;
      if (!userId) {
        setTableBookings([]);
        return;
      }
      setCurrentUserId(userId);

      let resData: any[] = [];
      const { data, error } = await supabase
        .from('table_reservations')
        .select('*, restaurant_tables(table_number, dining_area), branches(branch_name, address), tenants(business_name)')
        .eq('customer_user_id', userId)
        .order('reserved_for', { ascending: false });

      if (error) {
        const { data: fallbackData } = await supabase
          .from('table_reservations')
          .select('*')
          .eq('customer_user_id', userId)
          .order('reserved_for', { ascending: false });
        resData = fallbackData || [];
      } else {
        resData = data || [];
      }

      setTableBookings(resData);
    } catch (err) {
      console.warn('Failed to fetch table reservations:', err);
    } finally {
      setLoadingTableBookings(false);
    }
  };

  const fetchServiceBookings = async () => {
    setLoadingServiceBookings(true);
    try {
      const supabase = getSupabaseClient();
      if (!supabase) return;

      const { data: session } = await supabase.auth.getSession();
      const userId = session?.session?.user?.id;

      // 1. Try RPC get_my_service_bookings first
      const { data: rpcData, error: rpcErr } = await supabase.rpc('get_my_service_bookings');
      if (!rpcErr && rpcData && Array.isArray(rpcData)) {
        setServiceBookings(rpcData);
      } else if (userId) {
        // Fallback: direct query
        const { data: directData } = await supabase
          .from('service_bookings')
          .select(`
            id,
            booking_number,
            status,
            booking_mode,
            scheduled_at,
            quoted_price,
            final_price,
            quantity,
            customer_notes,
            created_at,
            start_otp,
            complete_otp,
            customer_name,
            customer_phone,
            customer_address,
            service_catalog_items (
              service_name,
              base_price
            )
          `)
          .eq('customer_user_id', userId)
          .order('created_at', { ascending: false });

        setServiceBookings(directData || []);
      }
    } catch (err) {
      console.warn('Failed to fetch service bookings:', err);
    } finally {
      setLoadingServiceBookings(false);
    }
  };

  const handleViewServiceDetail = async (booking: any) => {
    setSelectedServiceDetail(booking);
    // Also try calling get_my_service_booking_detail
    try {
      const supabase = getSupabaseClient();
      if (!supabase) return;
      setLoadingDetail(true);
      const { data, error } = await supabase.rpc('get_my_service_booking_detail', {
        p_booking_id: booking.id
      });
      if (!error && data) {
        setSelectedServiceDetail({ ...booking, ...data });
      }
    } catch (err) {
      console.warn('Detail fetch error:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    fetchBookings();
    fetchTableBookings();
    fetchServiceBookings();
  }, [userName, tenant]);

  const handlePayBooking = async (booking: any) => {
    const payAmt = Number(booking.amount_pending ?? booking.total_amount ?? 0);
    if (payAmt <= 0) {
      toast.info('No pending payment for this booking');
      return;
    }
    setPayingBookingId(booking.id);
    try {
      await payWithRazorpay({
        tenantId: booking.tenant_id,
        branchId: booking.branch_id || null,
        purpose: 'consumer_order',
        amount: payAmt,
        referenceType: 'room_booking',
        referenceId: booking.id,
        payerType: 'consumer',
        payerId: currentUserId,
        prefillName: booking.guest_name || '',
        prefillPhone: booking.guest_phone || '',
        onSuccess: async () => {
          toast.info('Confirming payment...');
          await pollPaymentStatus({
            table: 'room_bookings',
            id: booking.id,
            targetStatus: 'paid',
            maxAttempts: 4,
            delayMs: 1500
          });
          toast.success('Room booking payment confirmed!');
          await fetchBookings();
        },
        onFailure: (err) => {
          toast.info(err.message || 'Payment cancelled. You can try again anytime.');
        }
      });
    } catch (err: any) {
      console.warn('Booking pay error:', err);
    } finally {
      setPayingBookingId(null);
    }
  };

  const handlePayOrder = async (order: any) => {
    const payAmt = Number(order.amount_pending ?? order.total_amount ?? order.total ?? 0);
    if (payAmt <= 0) {
      toast.info('No pending payment for this order');
      return;
    }
    setPayingOrderId(order.id);
    try {
      await payWithRazorpay({
        tenantId: order.tenant_id,
        branchId: order.branch_id || null,
        purpose: 'consumer_order',
        amount: payAmt,
        referenceType: 'orders',
        referenceId: order.id,
        payerType: 'consumer',
        payerId: currentUserId,
        onSuccess: async () => {
          toast.info('Confirming payment...');
          await pollPaymentStatus({
            table: 'orders',
            id: order.id,
            targetStatus: 'paid',
            maxAttempts: 4,
            delayMs: 1500
          });
          toast.success('Order payment confirmed!');
          await fetchOrders();
        },
        onFailure: (err) => {
          toast.info(err.message || 'Payment cancelled');
        }
      });
    } catch (err: any) {
      console.warn('Order pay error:', err);
    } finally {
      setPayingOrderId(null);
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'New': return 'Placed';
      case 'Ready to Pack': return 'Packed';
      case 'Dispatch': return 'Dispatched';
      case 'Completed': return 'Delivered';
      default: return status;
    }
  };

  const getStatusStep = (status: string) => {
    switch (status) {
      case 'New': return 1;
      case 'Ready to Pack': return 2;
      case 'Dispatch': return 3;
      case 'Completed': return 4;
      default: return 0;
    }
  };

  return (
    <div className="space-y-5 max-w-2xl mx-auto">
      {/* Top Tabs */}
      <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3">
        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">My Activity</h2>
        <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-900 p-1 rounded-xl">
          <button
            onClick={() => setActiveTab('orders')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
              activeTab === 'orders'
                ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 shadow-sm'
                : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            <ShoppingBag size={14} />
            <span>Orders ({orders.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('bookings')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
              activeTab === 'bookings'
                ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 shadow-sm'
                : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            <Bed size={14} />
            <span>Room Bookings ({bookings.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('table_bookings')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
              activeTab === 'table_bookings'
                ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 shadow-sm'
                : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            <UtensilsCrossed size={14} />
            <span>Table Bookings ({tableBookings.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('services')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
              activeTab === 'services'
                ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 shadow-sm'
                : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            <Wrench size={14} />
            <span>Services ({serviceBookings.length})</span>
          </button>
        </div>
      </div>

      {/* ORDERS TAB */}
      {activeTab === 'orders' && (
        <>
          {loadingOrders ? (
            <div className="flex justify-center items-center h-40">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800 border-dashed">
              <ShoppingBag className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 dark:text-slate-400 font-medium">No past orders found.</p>
              <p className="text-xs text-slate-400 mt-1">When you place an order, it will appear here.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => {
                const step = getStatusStep(order.status);
                const isPaid = order.payment_status === 'paid';
                return (
                  <Card key={order.id} className="border border-slate-200 dark:border-slate-800 shadow-sm rounded-2xl overflow-hidden hover:shadow-md transition-shadow">
                    <CardContent className="p-0">
                      <div className="p-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 flex justify-between items-start">
                        <div>
                          <span className="text-xs font-bold px-2 py-1 rounded bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 mb-2 inline-block">
                            Order #{order.id.slice(0, 8).toUpperCase()}
                          </span>
                          <div className="flex items-center gap-1 text-slate-500 dark:text-slate-400 text-xs font-medium">
                            <Clock size={12} />
                            <span>{new Date(order.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        </div>
                        <div className="text-right space-y-1">
                          <p className="font-extrabold text-slate-900 dark:text-slate-100 text-lg">₹{order.total_amount}</p>
                          <div className="flex items-center justify-end gap-1.5">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${step >= 4 ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                              {getStatusLabel(order.status)}
                            </span>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isPaid ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                              {isPaid ? 'Paid' : 'Unpaid'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800">
                        <div className="flex items-center justify-between relative">
                          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-slate-200 dark:bg-slate-700 rounded-full z-0"></div>
                          <div className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-emerald-500 rounded-full z-0 transition-all duration-500" style={{ width: `${(Math.max(1, step) - 1) * 33.33}%` }}></div>
                          
                          <div className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${step >= 1 ? 'bg-emerald-500 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-400'}`}>1</div>
                          <div className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${step >= 2 ? 'bg-emerald-500 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-400'}`}>2</div>
                          <div className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${step >= 3 ? 'bg-emerald-500 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-400'}`}>3</div>
                          <div className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${step >= 4 ? 'bg-emerald-500 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-400'}`}>4</div>
                        </div>
                        <div className="flex justify-between mt-2 text-[10px] font-semibold text-slate-500 dark:text-slate-400">
                          <span className={step >= 1 ? 'text-emerald-700' : ''}>Placed</span>
                          <span className={step >= 2 ? 'text-emerald-700' : ''}>Packed</span>
                          <span className={step >= 3 ? 'text-emerald-700' : ''}>Shipped</span>
                          <span className={step >= 4 ? 'text-emerald-700' : ''}>Delivered</span>
                        </div>
                      </div>

                      {order.status === 'Dispatch' && order.delivery_assignment?.service_provider_id && ['assigned', 'picked_up'].includes(order.delivery_assignment.status) && (
                        <LiveTrackingMap deliveryAssignment={order.delivery_assignment} destinationAddress={order.delivery_address} />
                      )}
                      {order.delivery_assignment?.status === 'picked_up' && order.delivery_assignment?.delivery_otp && (
                        <div className="mx-4 mt-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-4 py-3 flex items-center justify-between">
                          <span className="text-xs font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wider">Share this code with your rider on delivery</span>
                          <span className="text-2xl font-extrabold text-amber-800 dark:text-amber-300 tracking-widest">{order.delivery_assignment.delivery_otp}</span>
                        </div>
                      )}

                      <div className="p-4 space-y-3 bg-white dark:bg-slate-950">
                        <p className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider mb-2">Items</p>
                        {order.order_items && order.order_items.map((item: any) => (
                          <div key={item.id} className="flex justify-between text-sm items-center">
                            <div className="flex gap-3 items-center">
                              <span className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 px-2 py-0.5 rounded font-bold text-xs">{item.quantity}x</span>
                              <span className="font-semibold text-slate-800 dark:text-slate-200">{item.products?.name || 'Unknown Product'}</span>
                            </div>
                            <span className="text-slate-600 dark:text-slate-400 font-medium">₹{item.price * item.quantity}</span>
                          </div>
                        ))}

                        {(!order.order_items || order.order_items.length === 0) && (
                          <p className="text-xs text-slate-400 italic">No items details available.</p>
                        )}

                        {order.delivery_address && (
                          <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800">
                            <p className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider mb-1">Delivery To</p>
                            <p className="text-sm text-slate-600 dark:text-slate-400">{order.delivery_address}</p>
                          </div>
                        )}

                        {/* Pay Now for unpaid online orders */}
                        {!isPaid && (
                          <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                            <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">Payment pending</span>
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold flex items-center gap-1.5"
                              disabled={payingOrderId === order.id}
                              onClick={() => handlePayOrder(order)}
                            >
                              {payingOrderId === order.id ? (
                                <>
                                  <Loader2 size={13} className="animate-spin" />
                                  <span>Opening Razorpay...</span>
                                </>
                              ) : (
                                <>
                                  <CreditCard size={13} />
                                  <span>Pay Now (₹{order.total_amount})</span>
                                </>
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ROOM BOOKINGS TAB */}
      {activeTab === 'bookings' && (
        <>
          {loadingBookings ? (
            <div className="flex justify-center items-center h-40">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : bookings.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800 border-dashed">
              <Bed className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 dark:text-slate-400 font-medium">No room bookings found.</p>
              <p className="text-xs text-slate-400 mt-1">Book a hotel room in the Stay section to see your reservations here.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking) => {
                const isPaid = booking.payment_status === 'paid';
                const isPaying = payingBookingId === booking.id;
                const hotelName = booking.branches?.branch_name || 'Hotel Stay';
                const roomType = booking.room_types?.name || 'Standard Room';

                return (
                  <Card key={booking.id} className="border border-slate-200 dark:border-slate-800 shadow-sm rounded-2xl overflow-hidden hover:shadow-md transition-shadow">
                    <CardContent className="p-0">
                      <div className="p-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 flex justify-between items-start">
                        <div>
                          <span className="text-xs font-bold px-2 py-1 rounded bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 mb-2 inline-block">
                            Booking #{booking.id.slice(0, 8).toUpperCase()}
                          </span>
                          <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">{hotelName}</h3>
                          {booking.branches?.address && (
                            <p className="text-xs text-slate-500 dark:text-slate-400">{booking.branches.address}</p>
                          )}
                        </div>
                        <div className="text-right space-y-1">
                          <p className="font-extrabold text-slate-900 dark:text-slate-100 text-lg">₹{booking.total_amount}</p>
                          <span
                            className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full inline-flex items-center gap-1 ${
                              isPaid
                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                                : 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                            }`}
                          >
                            {isPaid ? <CheckCircle2 size={11} /> : <AlertCircle size={11} />}
                            {isPaid ? 'Paid' : 'Payment Pending'}
                          </span>
                        </div>
                      </div>

                      <div className="p-4 space-y-3 bg-white dark:bg-slate-950">
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800">
                            <span className="text-slate-400 block mb-0.5">Room Type</span>
                            <span className="font-bold text-slate-800 dark:text-slate-200">{roomType}</span>
                          </div>
                          <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800">
                            <span className="text-slate-400 block mb-0.5">Guests</span>
                            <span className="font-bold text-slate-800 dark:text-slate-200">{booking.num_guests || 1} Person(s)</span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between text-xs text-slate-600 dark:text-slate-300 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800">
                          <div>
                            <span className="text-slate-400 block text-[10px]">Check-In</span>
                            <span className="font-bold">{booking.check_in_date}</span>
                          </div>
                          <span className="text-slate-400">→</span>
                          <div className="text-right">
                            <span className="text-slate-400 block text-[10px]">Check-Out</span>
                            <span className="font-bold">{booking.check_out_date}</span>
                          </div>
                        </div>

                        {booking.guest_name && (
                          <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center justify-between pt-1">
                            <span>Guest: <strong className="text-slate-700 dark:text-slate-300">{booking.guest_name}</strong></span>
                            {booking.guest_phone && <span>Phone: {booking.guest_phone}</span>}
                          </div>
                        )}

                        {/* Pay Now Button for Unpaid Room Bookings */}
                        {!isPaid && (
                          <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                            <div>
                              <p className="text-xs font-bold text-amber-700 dark:text-amber-400">Payment Pending</p>
                              <p className="text-[11px] text-slate-400">Pay securely via Razorpay to guarantee your room</p>
                            </div>
                            <Button
                              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold flex items-center gap-1.5"
                              disabled={isPaying}
                              onClick={() => handlePayBooking(booking)}
                            >
                              {isPaying ? (
                                <>
                                  <Loader2 size={14} className="animate-spin" />
                                  <span>Opening Razorpay...</span>
                                </>
                              ) : (
                                <>
                                  <CreditCard size={14} />
                                  <span>Pay Now (₹{booking.amount_pending ?? booking.total_amount})</span>
                                </>
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* TABLE BOOKINGS TAB */}
      {activeTab === 'table_bookings' && (
        <>
          {loadingTableBookings ? (
            <div className="flex justify-center items-center h-40">
              <Loader2 className="w-8 h-8 text-rose-500 animate-spin" />
            </div>
          ) : tableBookings.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-slate-900 rounded-2xl border border-dashed border-slate-200 dark:border-slate-800 p-8 shadow-sm">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-rose-50 dark:bg-rose-950/50 text-rose-500 flex items-center justify-center mb-3">
                <UtensilsCrossed size={26} />
              </div>
              <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">No table bookings yet</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-sm mx-auto">
                Reserve your table for lunch or dinner with indoor, outdoor, or AC seating options. Instant & free!
              </p>
              <Button
                onClick={() => window.location.href = '/book-table'}
                className="mt-4 bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs rounded-xl shadow-sm"
              >
                Book a Table Now
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {tableBookings.map((res: any) => {
                const restName = res.tenants?.business_name || res.branches?.branch_name || 'Restaurant';
                const branchAddr = res.branches?.address;
                const tblNum = res.restaurant_tables?.table_number;
                const dArea = res.dining_area_preference || res.restaurant_tables?.dining_area;
                const status = (res.status || 'confirmed').toLowerCase();
                const isConfirmed = status === 'confirmed';
                const isCancelled = status === 'cancelled';

                let formattedDate = res.reserved_for;
                try {
                  const d = new Date(res.reserved_for);
                  if (!isNaN(d.getTime())) {
                    formattedDate = d.toLocaleString('en-IN', {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    });
                  }
                } catch {}

                return (
                  <Card key={res.id} className="overflow-hidden border-slate-200 dark:border-slate-800 shadow-sm rounded-2xl">
                    <CardContent className="p-0">
                      <div className="p-4 bg-slate-50 dark:bg-slate-900/60 border-b border-slate-100 dark:border-slate-800 flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <UtensilsCrossed size={16} className="text-rose-500 shrink-0" />
                            <h3 className="font-bold text-slate-900 dark:text-slate-100 text-base">
                              {restName}
                            </h3>
                          </div>
                          {branchAddr && (
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 flex items-center gap-1">
                              <MapPin size={11} /> {branchAddr}
                            </p>
                          )}
                        </div>

                        <div className="text-right flex flex-col items-end gap-1">
                          <span
                            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold ${
                              isConfirmed
                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                                : isCancelled
                                ? 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300'
                                : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300'
                            }`}
                          >
                            {isConfirmed && <CheckCircle2 size={11} />}
                            {status.toUpperCase()}
                          </span>
                          <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
                            Free Reservation
                          </span>
                        </div>
                      </div>

                      <div className="p-4 space-y-3 bg-white dark:bg-slate-950">
                        <div className="grid grid-cols-3 gap-2 text-xs text-center">
                          <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800">
                            <span className="text-slate-400 block text-[10px] uppercase font-bold tracking-wider mb-0.5">Guests</span>
                            <span className="font-bold text-slate-800 dark:text-slate-200">{res.party_size} Person(s)</span>
                          </div>
                          <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800">
                            <span className="text-slate-400 block text-[10px] uppercase font-bold tracking-wider mb-0.5">Table</span>
                            <span className="font-bold text-slate-800 dark:text-slate-200">
                              {tblNum ? `Table ${tblNum}` : 'Reserved'}
                            </span>
                          </div>
                          <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800">
                            <span className="text-slate-400 block text-[10px] uppercase font-bold tracking-wider mb-0.5">Area</span>
                            <span className="font-bold text-slate-800 dark:text-slate-200">{dArea || 'Standard'}</span>
                          </div>
                        </div>

                        <div className="p-3 rounded-xl bg-rose-50/50 dark:bg-rose-950/20 border border-rose-100 dark:border-rose-900/40 flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <Clock size={15} className="text-rose-500 shrink-0" />
                            <div>
                              <span className="text-slate-400 block text-[10px] uppercase font-semibold">Reserved Time</span>
                              <span className="font-bold text-slate-800 dark:text-slate-200">{formattedDate}</span>
                            </div>
                          </div>
                          <span className="text-[10px] text-slate-500 font-medium">15 min grace period</span>
                        </div>

                        {res.special_request && (
                          <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-900 text-xs border border-slate-100 dark:border-slate-800">
                            <span className="text-slate-400 font-medium">Special Request: </span>
                            <span className="text-slate-700 dark:text-slate-300 font-medium">{res.special_request}</span>
                          </div>
                        )}

                        <div className="text-[11px] text-slate-400 flex items-center justify-between pt-1 border-t border-slate-100 dark:border-slate-800">
                          <span>Guest: <strong className="text-slate-600 dark:text-slate-400">{res.guest_name}</strong> {res.guest_phone && `(${res.guest_phone})`}</span>
                          <span>Ref: {res.id?.slice(0, 8)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* DAILY SERVICES TAB */}
      {activeTab === 'services' && (
        <>
          {loadingServiceBookings ? (
            <div className="flex justify-center items-center h-40">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : serviceBookings.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800 border-dashed">
              <Wrench className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 dark:text-slate-400 font-medium">No service bookings found.</p>
              <p className="text-xs text-slate-400 mt-1">When you book home or professional services, they will appear here.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {serviceBookings.map((b) => {
                const sName = b.service_name || b.service_catalog_items?.service_name || 'Daily Service';
                const qty = b.quantity && b.quantity > 0 ? b.quantity : 1;
                const statusColors: Record<string, string> = {
                  requested: 'bg-amber-100 text-amber-800 border-amber-200',
                  assigned: 'bg-blue-100 text-blue-800 border-blue-200',
                  accepted: 'bg-indigo-100 text-indigo-800 border-indigo-200',
                  in_progress: 'bg-purple-100 text-purple-800 border-purple-200 animate-pulse',
                  completed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
                  cancelled: 'bg-rose-100 text-rose-800 border-rose-200'
                };
                const statusCls = statusColors[b.status] || 'bg-slate-100 text-slate-800 border-slate-200';

                return (
                  <Card key={b.id} className="border border-slate-200 dark:border-slate-800 shadow-sm rounded-2xl overflow-hidden hover:shadow-md transition-shadow">
                    <CardContent className="p-0">
                      <div className="p-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 flex justify-between items-start">
                        <div>
                          <span className="text-xs font-bold px-2 py-0.5 rounded bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 mb-1.5 inline-block">
                            Booking #{b.booking_number || b.id.slice(0, 8).toUpperCase()}
                          </span>
                          <div className="flex items-center gap-1 text-slate-500 dark:text-slate-400 text-xs font-medium">
                            <Clock size={13} />
                            <span>{new Date(b.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                          </div>
                        </div>
                        <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full border uppercase tracking-wider ${statusCls}`}>
                          {b.status?.replace('_', ' ')}
                        </span>
                      </div>

                      <div className="p-4 space-y-3">
                        {/* Service Name & Quantity */}
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-bold text-sm text-slate-900 dark:text-white flex items-center gap-1.5">
                              <span>{sName}</span>
                              {qty > 1 && (
                                <span className="px-1.5 py-0.5 rounded-md bg-primary/10 text-primary text-xs font-extrabold">
                                  x {qty}
                                </span>
                              )}
                            </h4>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                              {b.booking_mode === 'on_demand' ? '⚡ On-Demand Service' : `📅 Scheduled: ${b.scheduled_at ? new Date(b.scheduled_at).toLocaleString() : 'Pending'}`}
                            </p>
                          </div>
                          <div className="text-right">
                            <span className="text-xs text-slate-400 block">Total</span>
                            <span className="text-sm font-bold text-slate-900 dark:text-white">
                              ₹{b.final_price || b.quoted_price || 0}
                            </span>
                          </div>
                        </div>

                        {/* Work Details / Customer Notes */}
                        {b.customer_notes && (
                          <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-900 text-xs border border-slate-100 dark:border-slate-800">
                            <span className="text-slate-400 font-bold block mb-0.5 text-[10px] uppercase tracking-wider">Work Details / Notes:</span>
                            <span className="text-slate-700 dark:text-slate-300 font-medium leading-relaxed">{b.customer_notes}</span>
                          </div>
                        )}

                        {/* OTP box if in progress or assigned */}
                        {(b.status === 'assigned' || b.status === 'accepted' || b.status === 'in_progress') && (
                          <div className="flex items-center gap-4 p-2.5 rounded-xl bg-primary/5 border border-primary/20 text-xs">
                            {b.start_otp && b.status !== 'in_progress' && (
                              <div>
                                <span className="text-[10px] text-slate-400 block font-bold">Start OTP</span>
                                <span className="font-mono font-bold text-primary tracking-widest">{b.start_otp}</span>
                              </div>
                            )}
                            {b.complete_otp && b.status === 'in_progress' && (
                              <div>
                                <span className="text-[10px] text-slate-400 block font-bold">Completion OTP</span>
                                <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400 tracking-widest">{b.complete_otp}</span>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="pt-2 flex justify-between items-center border-t border-slate-100 dark:border-slate-800">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewServiceDetail(b)}
                            className="text-xs font-semibold h-8"
                          >
                            View Details
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Service Booking Detail Modal */}
          {selectedServiceDetail && (
            <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-md max-h-[90vh] flex flex-col shadow-2xl overflow-hidden animate-in zoom-in-95 duration-150">
                <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center">
                  <div>
                    <h3 className="font-bold text-sm text-slate-900 dark:text-white">Service Booking Details</h3>
                    <span className="text-[11px] text-slate-400">#{selectedServiceDetail.booking_number || selectedServiceDetail.id?.slice(0, 8)}</span>
                  </div>
                  <button
                    onClick={() => setSelectedServiceDetail(null)}
                    className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="p-5 overflow-y-auto space-y-4 text-xs">
                  <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 space-y-1">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">Service</span>
                    <h4 className="font-bold text-sm text-slate-900 dark:text-white">
                      {selectedServiceDetail.service_name || selectedServiceDetail.service_catalog_items?.service_name || 'Daily Service'}
                      {(selectedServiceDetail.quantity && selectedServiceDetail.quantity > 1) ? ` x ${selectedServiceDetail.quantity}` : ''}
                    </h4>
                    <p className="text-slate-500 dark:text-slate-400 text-xs">
                      Status: <strong className="uppercase text-primary">{selectedServiceDetail.status}</strong>
                    </p>
                  </div>

                  {/* Quantity & Price */}
                  <div className="grid grid-cols-2 gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-800">
                    <div>
                      <span className="text-[10px] text-slate-400 block font-semibold uppercase">Quantity / Units</span>
                      <span className="font-bold text-sm text-slate-900 dark:text-white">
                        {selectedServiceDetail.quantity || 1} Unit(s)
                      </span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400 block font-semibold uppercase">Quoted Total</span>
                      <span className="font-bold text-sm text-emerald-600 dark:text-emerald-400">
                        ₹{selectedServiceDetail.final_price || selectedServiceDetail.quoted_price || 0}
                      </span>
                    </div>
                  </div>

                  {/* Work Details / Notes */}
                  {selectedServiceDetail.customer_notes && (
                    <div className="p-3.5 rounded-xl bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-800/30">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400 block mb-1">
                        Work Details / Notes
                      </span>
                      <p className="text-slate-800 dark:text-slate-200 leading-relaxed font-medium">
                        {selectedServiceDetail.customer_notes}
                      </p>
                    </div>
                  )}

                  {/* Address */}
                  {selectedServiceDetail.customer_address && (
                    <div className="p-3 rounded-xl border border-slate-200 dark:border-slate-800">
                      <span className="text-[10px] text-slate-400 block font-semibold uppercase mb-1">Service Address</span>
                      <p className="text-slate-700 dark:text-slate-300">{selectedServiceDetail.customer_address}</p>
                    </div>
                  )}
                </div>

                <div className="p-4 border-t border-slate-100 dark:border-slate-800 flex justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedServiceDetail(null)}
                    className="text-xs"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
